<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="questionnaireInstanceForm.id ? 'Edit evaluation' : $t('label.new_evaluation')"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          key="questionnaireInstanceFormInputQuestionnaire"
          rules="required"
        >
          <v-autocomplete
            v-model="questionnaireInstanceForm.questionnaire_id"
            outlined
            :items="questionnaires.data"
            :loading="questionnaires.loading"
            item-value="id"
            item-text="name"
            dense
            :label="$t('card_dialog.questionnaire.label')"
            :placeholder="$t('select_questionnaire.placeholder')"
            :error-messages="errors"
            clearable
          />
        </validation-provider>

        <subtitle class="mb-3 mt-4">
          <v-icon
            left
            small
          >
            fal fa-calendar-alt
          </v-icon>
          {{ $t('picker.date.title') }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="questionnaireInstanceFormInputDate"
          rules="required"
        >
          <v-menu
            v-model="fromDateActive"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="questionnaireInstanceForm.date ? $dayjs(questionnaireInstanceForm.date).format('MMM D, YYYY') : ''"
                :error-messages="errors"
                :label="$t('picker.date.placeholder')"
                outlined
                dense
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="questionnaireInstanceForm.date"
              :first-day-of-week="1"
              no-title
              @input="fromDateActive = false"
            />
          </v-menu>
        </validation-provider>

        <v-row
          no-gutters
          align="center"
          :class="{ 'mb-6': !questionnaireInstanceForm.repeats, 'mb-3': questionnaireInstanceForm.repeats }"
        >
          <v-col cols="auto">
            <subtitle>
              <v-icon
                left
                small
              >
                fal fa-refresh
              </v-icon>
              {{ $t('card_dialog.questionnaire_repeats') }}
            </subtitle>
          </v-col>
          <v-col
            cols="auto"
            class="pl-4"
          >
            <v-switch
              v-model="questionnaireInstanceForm.repeats"
              class="pa-0 ma-0"
              hide-details
              @change="processRepeatItemChange()"
            />
          </v-col>
          <template v-if="questionnaireInstanceForm.id && questionnaireInstanceForm.questionnaire_instance_schedule">
            <v-spacer />
            <v-col
              class="grey--text text-caption"
              cols="auto"
            >
              {{ $t("repeat_settings.label") }}
            </v-col>
          </template>
        </v-row>

        <div v-if="questionnaireInstanceForm.repeats">
          <validation-provider
            v-slot="{ errors }"
            key="eventFormInputRepeatMode"
            rules="required"
          >
            <v-select
              v-model="repeatMode"
              dense
              outlined
              :items="repeatItems"
              :label="$t('dropdown.repeat.title')"
              :error-messages="errors"
              @change="processRepeatItemChange"
            />
          </validation-provider>

          <div
            v-if="repeatMode === 'custom'"
          >
            <v-row no-gutters>
              <v-col
                cols="2"
                class="text-body-2 grey--text text--darken-2 pt-3"
              >
                {{ $t('repeat_settings.repeat.every') }}
              </v-col>
              <v-col
                cols="2"
                class="pl-4"
              >
                <validation-provider
                  v-slot="{ errors }"
                  key="eventFormInputRepeatInterval"
                  rules="required|numeric|min_value:1|max_value:100"
                >
                  <v-text-field
                    v-model="questionnaireInstanceForm.repeat_settings.repeat_interval"
                    dense
                    type="number"
                    min="1"
                    max="100"
                    hide-details
                    outlined
                    :error-messages="errors"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="3"
                class="pl-2"
              >
                <v-select
                  v-model="questionnaireInstanceForm.repeat_settings.repeat_unit"
                  dense
                  outlined
                  :items="repeatUnitItems"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="questionnaireInstanceForm.repeat_settings.repeat_unit === 'week'"
              no-gutters
              align="center"
              class="mb-6"
            >
              <v-col
                cols="2"
                class="text-body-2 grey--text text--darken-2"
              >
                {{ $t('repeat_settings.repeat.repeat_on') }}
              </v-col>
              <v-col
                class="pl-4"
              >
                <validation-provider
                  v-slot="{ errors }"
                  key="eventFormInputRepeatWeekdays"
                  rules="required"
                >
                  <v-btn-toggle
                    v-model="questionnaireInstanceForm.repeat_settings.constraints.weekdays"
                    dense
                    multiple
                    mandatory
                    color="primary"
                  >
                    <v-btn
                      :value="1"
                      class="text-none"
                    >
                      {{ $t('day.mon') }}
                    </v-btn>
                    <v-btn
                      :value="2"
                      class="text-none"
                    >
                      {{ $t('day.tue') }}
                    </v-btn>
                    <v-btn
                      :value="3"
                      class="text-none"
                    >
                      {{ $t('day.wed') }}
                    </v-btn>
                    <v-btn
                      :value="4"
                      class="text-none"
                    >
                      {{ $t('day.thu') }}
                    </v-btn>
                    <v-btn
                      :value="5"
                      class="text-none"
                    >
                      {{ $t('day.fri') }}
                    </v-btn>
                    <v-btn
                      :value="6"
                      class="text-none"
                    >
                      {{ $t('day.sat') }}
                    </v-btn>
                    <v-btn
                      :value="7"
                      class="text-none"
                    >
                      {{ $t('day.sun') }}
                    </v-btn>
                  </v-btn-toggle>
                  <div
                    v-if="errors"
                    class="caption error--text"
                  >
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row
              v-if="questionnaireInstanceForm.repeat_settings.repeat_unit === 'month'"
              no-gutters
              align="center"
              class="mb-6"
            >
              <v-col
                cols="2"
                class="text-body-2 grey--text text--darken-2"
              >
                {{ $t('dropdown.repeat.title') }}
              </v-col>
              <v-col
                class="pl-4"
              >
                <v-select
                  v-model="questionnaireInstanceForm.repeat_settings.constraints.monthly_mode"
                  :items="monthlyModeItems"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
          </div>

          <div class="text-body-2 grey--text text--darken-2">
            {{ $t('repeat_settings.ends') }}
          </div>
          <v-radio-group
            v-model="questionnaireInstanceForm.repeat_settings.end_mode"
            class="mb-2"
          >
            <v-row
              no-gutters
              align="center"
              class="mb-1"
              style="height: 40px"
            >
              <v-col cols="auto">
                <v-radio
                  value="never"
                />
              </v-col>
              <v-col
                class="grey--text text--lighten-1"
                :class="{ 'text--darken-2': questionnaireInstanceForm.repeat_settings.end_mode === 'never'}"
              >
                {{ $t('repeat_settings.repeat.never') }}
              </v-col>
            </v-row>
            <v-row
              no-gutters
              align="center"
              class="mb-1"
            >
              <v-col cols="auto">
                <v-radio
                  value="end_at"
                />
              </v-col>
              <v-col
                class="grey--text text--lighten-1"
                :class="{ 'text--darken-2': questionnaireInstanceForm.repeat_settings.end_mode === 'end_at'}"
              >
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col cols="2">
                    {{ $t('repeat_settings.repeat.end_on') }}
                  </v-col>
                  <v-col
                    cols="4"
                    class="pl-4"
                  >
                    <v-menu
                      v-model="repeatEndAtCalendar"
                      :close-on-content-click="false"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="questionnaireInstanceForm.repeat_settings.end_at ? $dayjs(questionnaireInstanceForm.repeat_settings.end_at).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                          dense
                          outlined
                          hide-details
                          readonly
                          :disabled="questionnaireInstanceForm.repeat_settings.end_mode !== 'end_at'"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="questionnaireInstanceForm.repeat_settings.end_at"
                        :first-day-of-week="1"
                        no-title
                        :min="questionnaireInstanceForm.date ? $dayjs(questionnaireInstanceForm.date).format('YYYY-MM-DD') : undefined"
                        @input="repeatEndAtCalendar = false"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              align="center"
            >
              <v-col cols="auto">
                <v-radio
                  value="end_after_occurrences"
                />
              </v-col>
              <v-col
                class="grey--text text--lighten-1"
                :class="{ 'text--darken-2': questionnaireInstanceForm.repeat_settings.end_mode === 'end_after_occurrences'}"
              >
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col cols="2">
                    {{ $t('repeat_settings.repeat.end_after') }}
                  </v-col>
                  <v-col
                    cols="4"
                    class="pl-4"
                  >
                    <v-text-field
                      v-model="questionnaireInstanceForm.repeat_settings.end_after_occurrences"
                      dense
                      outlined
                      hide-details
                      :suffix="$t('text_input.end_after.suffix')"
                      :disabled="questionnaireInstanceForm.repeat_settings.end_mode !== 'end_after_occurrences'"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-radio-group>
        </div>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user-group
          </v-icon> {{ $t('add_new.team') }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="questionnaireInstanceFormInputTeams"
          rules="required"
        >
          <v-autocomplete
            v-model="questionnaireInstanceForm.team_ids"
            outlined
            :search-input.sync="searchInputTeams"
            :items="teamSelectItems"
            item-value="id"
            item-text="name"
            dense
            multiple
            :error-messages="errors"
            :placeholder="$t('select_team.placeholder')"
          >
            <template v-slot:selection="{item}">
              {{ item.name }}
            </template>
          </v-autocomplete>
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user
          </v-icon> {{ $t("dropdown.players.title") }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="questionnaireInstanceFormInputPlayers"
          rules="required"
        >
          <v-autocomplete
            v-model="questionnaireInstanceForm.player_ids"
            outlined
            :search-input.sync="searchInputPlayers"
            :items="playersToSelect"
            item-value="id"
            item-text="name"
            dense
            multiple
            :chips="false"
            :error-messages="errors"
            clearable
            :placeholder="$t('dropdown.players.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 2"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 2">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 2"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
            <template v-slot:item="{ item, on }">
              <v-list-item-action>
                <v-simple-checkbox
                  :value="questionnaireInstanceForm.player_ids.indexOf(item.id) > -1"
                  :ripple="false"
                  v-on="on"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="item.teams && item.teams.length">{{ item.teams.map(t => t.short_name).join(', ') }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  > &middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </validation-provider>
      </validation-observer>
    </v-card-text>

    <v-card-actions class="pa-6 pt-0">
      <v-btn
        v-if="questionnaireInstanceForm.id && $can('edit-event')"
        color="error"
        text
        @click="initDelete()"
      >
        {{ $t('button.delete') }}
      </v-btn>
      <v-spacer />
      <v-btn
        text
        @click="modalVisible = false"
      >
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        {{ $t('button.save') }}
      </v-btn>
    </v-card-actions>
    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteQuestionnaireInstance()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import { PlayerApiService, QuestionnaireApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import { cloneDeep, difference, union } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import dayjs from 'dayjs'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { getOrdinal, getDateTimeFormatForUser } from '@/utils/helpers'
import i18n from '@/plugins/i18n'

const emptyForm = {
  id: null,
  questionnaire_id: null,
  date: dayjs().format('YYYY-MM-DD'),
  team_ids: [],
  player_ids: [],
  repeats: false,
  repeat_settings: {
    repeat_unit: 'week',
    repeat_interval: 1,
    constraints: {
      weekdays: [],
      monthly_mode: 'weekday'
    },
    end_mode: 'never',
    end_after_occurrences: 12,
    end_at: null
  }
}

export default {
  name: 'QuestionnaireInstanceFormDialog',
  components: { Subtitle, CardDialog, DeleteConfirmationDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    questionnaireInstance: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fromDateActive: false,
      questionnaireInstanceForm: cloneDeep(emptyForm),
      submitting: false,
      searchInputTeams: '',
      searchInputPlayers: '',
      suppressIdsWatch: false,
      players: {
        data: [],
        loading: false,
        eventPlayers: []
      },
      questionnaires: {
        data: [],
        loading: false
      },
      weekdays: [
        { value: 1, text: 'Mon' },
        { value: 2, text: 'Tue' },
        { value: 3, text: 'Wed' },
        { value: 4, text: 'Thu' },
        { value: 5, text: 'Fri' },
        { value: 6, text: 'Sat' },
        { value: 7, text: 'Sun' }
      ],
      repeatMode: 'weekly',
      repeatEndAtCalendar: false,
      repeatEditDialog: {
        visible: false,
        mode: 'all',
        deleting: false
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedQuestionnaire() {
      if (!this.questionnaireInstanceForm.questionnaire_id) return undefined

      return this.questionnaires.data.find(q => q.id === this.questionnaireInstanceForm.questionnaire_id)
    },
    teamSelectItems() {
      if (!this.selectedQuestionnaire) return []

      return this.selectedQuestionnaire.teams.filter(qt => !!qt.is_active)
    },
    playersToSelect() {
      return this.players.data.slice(0).sort((a, b) => {
        const aSelected = this.questionnaireInstanceForm.player_ids.indexOf(a.id) > -1
        const bSelected = this.questionnaireInstanceForm.player_ids.indexOf(b.id) > -1

        if (aSelected && !bSelected) return -1
        else if (!aSelected && bSelected) return 1

        return 0
      })
    },
    repeatItems() {
      if (!this.questionnaireInstanceForm.date) return []

      const eventDate = this.$dayjs(this.questionnaireInstanceForm.date)

      return [
        { value: 'daily', text: i18n.t('repeat_settings.repeat.every_day') },
        { value: 'weekly', text: i18n.t('repeat_settings.repeat.every_week') + ' ' + eventDate.format('dddd') },
        { value: 'monthly', text: i18n.t('repeat_settings.repeat.every_month') + ' ' + getOrdinal(Math.ceil(eventDate.date() / 7)) + ' ' + eventDate.format('dddd') },
        { value: 'yearly', text: i18n.t('repeat_settings.repeat.every_year') + ' ' + eventDate.format('MMM DD') },
        { value: 'custom', text: i18n.t('repeat_settings.repeat.custom') }
      ]
    },
    repeatUnitItems() {
      return [
        { value: 'day', text: this.$tc('repeat_settings.day', parseInt(this.questionnaireInstanceForm.repeat_settings.repeat_interval)) },
        { value: 'week', text: this.$tc('repeat_settings.week', parseInt(this.questionnaireInstanceForm.repeat_settings.repeat_interval)) },
        { value: 'month', text: this.$tc('repeat_settings.month', parseInt(this.questionnaireInstanceForm.repeat_settings.repeat_interval)) },
        { value: 'year', text: this.$tc('repeat_settings.year', parseInt(this.questionnaireInstanceForm.repeat_settings.repeat_interval)) }
      ]
    },
    monthlyModeItems() {
      if (!this.questionnaireInstanceForm.date) return []

      const eventDate = this.$dayjs(this.questionnaireInstanceForm.date)

      return [
        { value: 'weekday', text: this.$t('repeat_settings.monthly.options.weekday', { ord: getOrdinal(Math.ceil(eventDate.date() / 7)), day: eventDate.format('dddd') }) },
        { value: 'day', text: this.$t('repeat_settings.monthly.options.day', { ord: getOrdinal(Math.ceil(eventDate.date() / 7)) }) }
      ]
    }
  },
  watch: {
    'questionnaireInstanceForm.team_ids': function (newValue, oldValue) {
      this.searchInputTeams = ''

      if (this.suppressIdsWatch) return

      const addedTeamIds = difference(newValue, oldValue)
      const removedTeamIds = difference(oldValue, newValue)

      const removePlayerIds = this.playersToSelect.filter(p => (p.team_ids || []).some(tid => removedTeamIds.includes(tid)) && !(p.team_ids || []).some(tid => newValue.includes(tid))).map(p => p.id)
      const addPlayerIds = this.playersToSelect.filter(p => (p.team_ids || []).some(tid => addedTeamIds.includes(tid))).map(p => p.id)

      this.questionnaireInstanceForm.player_ids = union(difference(this.questionnaireInstanceForm.player_ids, removePlayerIds), addPlayerIds)
    },
    'questionnaireInstanceForm.player_ids': function () {
      this.searchInputPlayers = ''
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_QUESTIONNAIRE_INSTANCE_FORM, () => {
      this.resetForm()

      this.fetchAllQuestionnaires()
      this.fetchAllPlayers()

      this.modalVisible = true
    })

    EventBus.$on(this.$events.EDIT_QUESTIONNAIRE_INSTANCE, (questionnaireInstanceId) => {
      this.resetForm()

      this.fetchAllQuestionnaires()
      this.fetchAllPlayers()

      if (questionnaireInstanceId) {
        this.loadInstance(questionnaireInstanceId)
        this.fetchAllPlayers()
        this.modalVisible = true
      }
    })
  },
  methods: {
    getDateTimeFormatForUser,
    resetForm() {
      this.questionnaireInstanceForm = cloneDeep(emptyForm)
      this.searchInputTeams = ''
      this.searchInputPlayers = ''
      this.suppressIdsWatch = false
    },
    async loadInstance(questionnaireInstanceId) {
      try {
        this.suppressIdsWatch = true

        const response = await QuestionnaireApiService.findInstance(questionnaireInstanceId)
        this.questionnaireInstanceForm = {
          id: response.data.id,
          questionnaire_id: response.data.questionnaire_id,
          date: response.data.date,
          team_ids: response.data.teams ? response.data.teams.map(team => team.id) : [],
          player_ids: response.data.players ? response.data.players.map(player => player.id) : []
        }

        this.players.eventPlayers = response.data.players || []

        this.$nextTick(() => { this.suppressIdsWatch = false })
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There was an error loading the evaluation',
          color: 'error'
        })
      }
    },
    async fetchAllQuestionnaires() {
      try {
        this.questionnaires.loading = true

        const result = await QuestionnaireApiService.index({ is_scheduled: true, is_active: true })

        this.questionnaires.data = result.data
      } finally {
        this.questionnaires.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    initDelete() {
      this.deleteConfirmationDialogVisible = true
    },
    async deleteQuestionnaireInstance() {
      try {
        this.deleting = true

        await QuestionnaireApiService.deleteInstance(this.questionnaireInstanceForm.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('success.evaluation_deleted'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false
        EventBus.$emit(this.$events.QUESTIONNAIRE_INSTANCE_DELETED, this.questionnaireInstanceForm)
        this.modalVisible = false
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.delete_evaluation'),
          color: 'error'
        })
      } finally {
        this.deleting = false
      }
    },
    async submit() {
      const isValid = await this.$refs.validation.validate()

      if (!isValid) {
        return
      }

      this.submitting = true

      try {
        let response
        const formData = {
          questionnaire_id: this.questionnaireInstanceForm.questionnaire_id,
          date: this.questionnaireInstanceForm.date,
          team_ids: this.questionnaireInstanceForm.team_ids,
          player_ids: this.questionnaireInstanceForm.player_ids,
          repeats: this.questionnaireInstanceForm.repeats,
          repeat_settings: this.questionnaireInstanceForm.repeats ? this.questionnaireInstanceForm.repeat_settings : null
        }

        if (this.questionnaireInstanceForm.id) {
          response = await QuestionnaireApiService.updateInstance(this.questionnaireInstanceForm.id, formData)
          EventBus.$emit(this.$events.QUESTIONNAIRE_INSTANCE_UPDATED, response.data)
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('success.evaluation_updated'),
            color: 'success'
          })
        } else {
          response = await QuestionnaireApiService.createInstance(formData)
          EventBus.$emit(this.$events.QUESTIONNAIRE_INSTANCE_CREATED, response.data)
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('success.evaluation_created'),
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.save_evaluation'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    processRepeatItemChange() {
      this.questionnaireInstanceForm.repeat_settings.end_at = dayjs(this.questionnaireInstanceForm.date).add(1, 'year').format('YYYY-MM-DD')

      if (this.repeatMode === 'daily') {
        this.questionnaireInstanceForm.repeat_settings.repeat_unit = 'day'
        this.questionnaireInstanceForm.repeat_settings.repeat_interval = 1
        return
      }

      if (this.repeatMode === 'weekly') {
        this.questionnaireInstanceForm.repeat_settings.repeat_unit = 'week'
        this.questionnaireInstanceForm.repeat_settings.repeat_interval = 1

        const day = this.questionnaireInstanceForm.date ? dayjs(this.questionnaireInstanceForm.date).day() : 1
        this.questionnaireInstanceForm.repeat_settings.constraints.weekdays = [day === 0 ? 7 : day]

        return
      }

      if (this.repeatMode === 'monthly') {
        this.questionnaireInstanceForm.repeat_settings.repeat_unit = 'month'
        this.questionnaireInstanceForm.repeat_settings.repeat_interval = 1
        this.questionnaireInstanceForm.repeat_settings.constraints.monthly_mode = 'weekday'
        return
      }

      if (this.repeatMode === 'yearly') {
        this.questionnaireInstanceForm.repeat_settings.repeat_unit = 'year'
        this.questionnaireInstanceForm.repeat_settings.repeat_interval = 1
      }
    },
    loadRepeatSchedule() {
      if (!this.questionnaireInstanceForm.questionnaire_instance_schedule) return

      this.questionnaireInstanceForm.repeats = true

      const repeatSettings = {
        repeat_unit: this.questionnaireInstanceForm.questionnaire_instance_schedule.repeat_unit,
        repeat_interval: this.questionnaireInstanceForm.questionnaire_instance_schedule.repeat_interval,
        constraints: this.questionnaireInstanceForm.questionnaire_instance_schedule.constraints,
        end_after_occurrences: this.questionnaireInstanceForm.questionnaire_instance_schedule.end_after_occurrences || 12,
        end_at: this.questionnaireInstanceForm.questionnaire_instance_schedule.end_at || dayjs(this.questionnaireInstanceForm.date).add(1, 'year').format('YYYY-MM-DD'),
        end_mode: this.questionnaireInstanceForm.questionnaire_instance_schedule.end_at ? 'end_at' : (this.questionnaireInstanceForm.questionnaire_instance_schedule.end_after_occurrences ? 'end_after_occurrences' : 'never')
      }

      this.questionnaireInstanceForm.repeat_settings = repeatSettings

      if (repeatSettings.repeat_unit === 'day' && repeatSettings.repeat_interval === 1) this.repeatMode = 'daily'
      else if (repeatSettings.repeat_unit === 'week' && repeatSettings.repeat_interval === 1 && repeatSettings.constraints.weekdays.length === 1) this.repeatMode = 'weekly'
      else if (repeatSettings.repeat_unit === 'month' && repeatSettings.repeat_interval === 1 && repeatSettings.constraints.monthly_mode === 'weekday') this.repeatMode = 'monthly'
      else if (repeatSettings.repeat_unit === 'year' && repeatSettings.repeat_interval === 1) this.repeatMode = 'yearly'
      else this.repeatMode = 'custom'
    }
  }
}
</script>
